import { Component } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { StyledCheckboxComponent } from '../shared/components/styled-checkbox/styled-checkbox.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';

import { MatCardModule } from '@angular/material/card';
import { NgFor, NgIf } from '@angular/common';
import { MatRadioButton, MatRadioModule } from '@angular/material/radio';
import { RestService } from '../services/rest.service';

import { BrandService } from '../../services/brand-config.service';
import { TranslationService } from '../services/translation.service';
import { TranslatePipe } from '../pipes/translate.pipe';

@Component({
  selector: 'app-delete-user',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    StyledCheckboxComponent,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCardModule,
    NgIf,
    MatRadioButton,
    MatRadioModule,
    NgFor,
    TranslatePipe,
  ],
  templateUrl: './delete-user.component.html',
  styleUrl: './delete-user.component.scss',
})
export class DeleteUserComponent {
  form: FormGroup;
  specificInfoRequired = false;
  emailNotFound: boolean = false;
  isSuccessMessage = false;
  isSubmitting = false;
  // `label` stays as the canonical English string (used as the submitted
  // payload key); `labelKey` is the i18n key used only for display.
  reasons = [
    {
      label:
        'The information is no longer necessary for the purpose it was collected',
      labelKey: 'deleteUser.reasons.noLongerNecessary',
    },
    {
      label: 'No longer consenting to the processing of data',
      labelKey: 'deleteUser.reasons.noLongerConsenting',
    },
    {
      label:
        'Objection to the processing of data in accordance with Article 21 of the GDPR',
      labelKey: 'deleteUser.reasons.objectionArticle21',
    },
    {
      label: 'Did not give consent to the processing of personal data',
      labelKey: 'deleteUser.reasons.noConsentGiven',
    },
    {
      label: 'You are, or the data subject is a minor',
      labelKey: 'deleteUser.reasons.minor',
    },
    { label: 'Other', labelKey: 'deleteUser.reasons.other' },
  ];

  informationToEraseArray = [
    { label: 'First name', labelKey: 'deleteUser.informationToErase.firstName' },
    { label: 'Last name', labelKey: 'deleteUser.informationToErase.lastName' },
    {
      label: 'Email address',
      labelKey: 'deleteUser.informationToErase.emailAddress',
    },
    {
      label: 'Captured image',
      labelKey: 'deleteUser.informationToErase.capturedImage',
    },
    {
      label: 'Health-related information',
      labelKey: 'deleteUser.informationToErase.healthInfo',
    },
  ];

  constructor(
    private fb: FormBuilder,
    private restService: RestService,
    public brandService: BrandService,
    public translationService: TranslationService
  ) {
    this.form = this.fb.group({
      fullName: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      selected_reasons: this.fb.array(
        this.reasons.map((item) =>
          this.fb.group({ label: item.label, value: false })
        )
      ),
      specificInfoToErase: this.fb.array(
        this.informationToEraseArray.map((item) =>
          this.fb.group({ label: item.label, value: false })
        )
      ),
      informationErasure: ['', [Validators.required]],
      custom_reason: [''], // For the "Other" reason text input
    });
  }
  get reason() {
    return this.form.get('selected_reasons') as FormArray;
  }
  get specificInfoToErase() {
    return this.form.get('specificInfoToErase') as FormArray;
  }

  onEraseTypeChange(value: string) {
    if (value === 'erase_all') {
      this.specificInfoRequired = false;
    }
  }

  onSubmit() {
    if (this.isSubmitting) return;
    if (this.form.invalid) {
      // Trigger validation
      this.isSubmitting = false;
      this.form.markAllAsTouched(); // Ensures that the error message will show on submission
    }
    if (this.form.get('informationErasure')?.value === 'specific_info') {
      const isAnyInformationSelected = this.specificInfoToErase.controls.some(
        (ctrl) => ctrl.get('value')?.value
      );
      if (!isAnyInformationSelected) {
        this.specificInfoRequired = true;
        return;
      } else {
        this.specificInfoRequired = false;
      }
    }
    if (this.form.valid) {
      this.isSubmitting = true;
      this.saveData(this.form.value);
    }
  }
  async saveData(data) {
    this.emailNotFound = false; // Reset the error on success
    // setting payload
    let payload = data;
    payload['selected_reasons'] = {
      ...data['selected_reasons'].reduce((acc, reason) => {
        acc[reason.label] = reason.value;
        return acc;
      }, {} as { [key: string]: boolean }),
    };
    payload['specificInfoToErase'] = {
      ...data['specificInfoToErase'].reduce((acc, reason) => {
        acc[reason.label] = reason.value;
        return acc;
      }, {} as { [key: string]: boolean }),
    };

    // api calling
    try {
      const response: any = await this.restService.request({
        path: `data-deletion/create`,
        method: 'POST',
        body: { data },
      });

      if (
        response &&
        response.data === 'Data deletion request sent successfully'
      ) {
        this.form.reset();
        this.isSuccessMessage = true;
      }
    } catch (error) {
      console.error('Failed to save delete request:', error);
    } finally {
      this.isSubmitting = false;
    }
  }
}
